import { createDictionary, uniq } from 'utils/ObjectUtils';

import { EXTENSIONS_ENUM } from './ExtensionConstants';
import { FEATURES } from './FeatureConstants';
import { INTERVENTION_MODE } from './InterventionConstants';
import { PATH } from './RoutesConstants';
import type { ExtensionEnum } from '../modules/global/typing/enums/extensions.enum';
import type { ModeEnum } from '../modules/global/typing/enums/modes.enum';
import type { ModuleEnum } from '../modules/global/typing/enums/module.enum';

// TECHNICIAN
export const TIRES = 'TIRES';
export const MECHANIC = 'MECHANIC';
export const BODYWORK = 'BODYWORK';
export const REMINDER = 'REMINDER';
export const GUARANTEE = 'GUARANTEE';
export const COMMUNICATION = 'COMMUNICATION';
export const BODYWORK_APPOINTMENT = 'BODYWORK_APPOINTMENT';

// SALES
export const STOCK_VO = 'STOCK_VO';
export const SALES_MESSAGE = 'SALES_MESSAGE';

// PARTS
export const PARTS = 'PARTS';
export const PARTS_REMINDER = 'PARTS_REMINDER';

export const REMINDER_MODULE_BY_MODE = createDictionary({
  [INTERVENTION_MODE.PARTS]: PARTS_REMINDER
}, REMINDER);

interface ModuleType {
  mode: ModeEnum; // The mode of the module
  extensions: string[]; // The list of extensions that can access this module
  features?: string[]; // The list of features that can access this module
  icon: string; // fb-icon representing the module
  hasPayment: boolean; // True if this module can have payments
  hasFollowUp: boolean; // True if this module can have follow-up
  canCreateReminder: boolean; // True if this module can generate a reminder with the refused advices
  canAddPreApproved: boolean; // True if this module can have pre-approved advices
  canAcceptManually: boolean; // True if user can accept manually advices of this module
  canSendCampaign: boolean; // True if user can use this module in the campaign
  canAddMessage: boolean; // True if user can add a message
  hasTireReport: boolean; // True if this module has tire report feature
  dashboard: string; // Path to open the dashboard of this module
  path: string; // Path to open a file in this module
}

/**
 * Dictionary of modules, allows to gather all their characteristics in one place.
 */
export const MODULES: Record<ModuleEnum, ModuleType> = {
  [GUARANTEE]: {
    mode: INTERVENTION_MODE.TECHNICIAN,
    extensions: [EXTENSIONS_ENUM.AFTER_SALES],
    icon: 'fb-icon--module-garantee',
    hasPayment: false,
    hasFollowUp: true,
    canAddMessage: true,
    canSendCampaign: true,
    canCreateReminder: false,
    canAddPreApproved: true,
    canAcceptManually: true,
    hasTireReport: true,
    dashboard: PATH.AFTERSALES.path,
    path: PATH.AFTERSALES_DETAILED.path
  },
  [BODYWORK]: {
    mode: INTERVENTION_MODE.TECHNICIAN,
    extensions: [EXTENSIONS_ENUM.AFTER_SALES],
    icon: 'fb-icon--module-bodywork',
    hasPayment: true,
    hasFollowUp: true,
    canAddMessage: true,
    canSendCampaign: true,
    canCreateReminder: true,
    canAddPreApproved: true,
    canAcceptManually: true,
    hasTireReport: true,
    dashboard: PATH.AFTERSALES.path,
    path: PATH.AFTERSALES_DETAILED.path
  },
  [MECHANIC]: {
    mode: INTERVENTION_MODE.TECHNICIAN,
    extensions: [EXTENSIONS_ENUM.AFTER_SALES],
    icon: 'fb-icon--module-mechanic',
    hasPayment: true,
    hasFollowUp: true,
    canAddMessage: true,
    canSendCampaign: true,
    canCreateReminder: true,
    canAddPreApproved: true,
    canAcceptManually: true,
    hasTireReport: true,
    dashboard: PATH.AFTERSALES.path,
    path: PATH.AFTERSALES_DETAILED.path
  },
  [TIRES]: {
    mode: INTERVENTION_MODE.TECHNICIAN,
    extensions: [EXTENSIONS_ENUM.AFTER_SALES],
    icon: 'fb-icon--module-tire',
    hasPayment: true,
    hasFollowUp: true,
    canAddMessage: true,
    canSendCampaign: true,
    canCreateReminder: true,
    canAddPreApproved: true,
    canAcceptManually: true,
    hasTireReport: true,
    dashboard: PATH.AFTERSALES.path,
    path: PATH.AFTERSALES_DETAILED.path
  },
  [COMMUNICATION]: {
    mode: INTERVENTION_MODE.TECHNICIAN,
    extensions: [EXTENSIONS_ENUM.AFTER_SALES],
    icon: 'fb-icon--module-communication',
    hasPayment: true,
    hasFollowUp: true,
    canAddMessage: true,
    canSendCampaign: true,
    canCreateReminder: true,
    canAddPreApproved: true,
    canAcceptManually: true,
    hasTireReport: true,
    dashboard: PATH.AFTERSALES.path,
    path: PATH.AFTERSALES_DETAILED.path
  },
  [BODYWORK_APPOINTMENT]: {
    mode: INTERVENTION_MODE.TECHNICIAN,
    extensions: [EXTENSIONS_ENUM.AFTER_SALES],
    features: [FEATURES.BODYWORK_APPOINTMENT],
    icon: 'fb-icon--module-chatbot',
    hasPayment: false,
    hasFollowUp: false,
    canAddMessage: false,
    canSendCampaign: false,
    canCreateReminder: false,
    canAddPreApproved: false,
    canAcceptManually: false,
    hasTireReport: true,
    dashboard: PATH.BODYWORKS.path,
    path: PATH.BODYWORKS_DETAILED.path
  },
  [REMINDER]: {
    mode: INTERVENTION_MODE.TECHNICIAN,
    extensions: [EXTENSIONS_ENUM.AFTER_SALES],
    features: [FEATURES.REMINDER],
    icon: 'fb-icon--module-reminder',
    hasPayment: false,
    hasFollowUp: false,
    canAddMessage: true,
    canSendCampaign: false,
    canCreateReminder: false,
    canAddPreApproved: false,
    canAcceptManually: true,
    hasTireReport: false,
    dashboard: PATH.REMINDERS.path,
    path: PATH.REMINDERS_DETAILED.path
  },
  [PARTS]: {
    mode: INTERVENTION_MODE.PARTS,
    extensions: [EXTENSIONS_ENUM.PARTS],
    icon: 'fb-icon--module-parts',
    hasPayment: true,
    hasFollowUp: true,
    canAddMessage: true,
    canSendCampaign: true,
    canCreateReminder: true,
    canAddPreApproved: true,
    canAcceptManually: true,
    hasTireReport: true,
    dashboard: PATH.PARTS.path,
    path: PATH.PARTS_DETAILED.path
  },
  [PARTS_REMINDER]: {
    mode: INTERVENTION_MODE.PARTS,
    extensions: [EXTENSIONS_ENUM.PARTS],
    features: [FEATURES.PARTS_REMINDER],
    icon: 'fb-icon--module-reminder',
    hasPayment: false,
    hasFollowUp: false,
    canAddMessage: true,
    canSendCampaign: false,
    canCreateReminder: false,
    canAddPreApproved: false,
    canAcceptManually: true,
    hasTireReport: false,
    dashboard: PATH.PARTS_REMINDER.path,
    path: PATH.PARTS_REMINDER_DETAILED.path
  },
  [SALES_MESSAGE]: {
    mode: INTERVENTION_MODE.SALES,
    extensions: [EXTENSIONS_ENUM.SALES],
    icon: 'fb-icon--module-direct-message',
    hasPayment: true,
    hasFollowUp: true,
    canAddMessage: true,
    canSendCampaign: true,
    canCreateReminder: false,
    canAddPreApproved: true,
    canAcceptManually: true,
    hasTireReport: false,
    dashboard: PATH.SALES.path,
    path: PATH.SALES_DETAILED.path
  },
  [STOCK_VO]: {
    mode: INTERVENTION_MODE.SALES,
    extensions: [EXTENSIONS_ENUM.SALES],
    icon: 'fb-icon--module-stock-vo',
    hasPayment: false,
    hasFollowUp: false,
    canAddMessage: false,
    canSendCampaign: false,
    canCreateReminder: false,
    canAddPreApproved: false,
    canAcceptManually: false,
    hasTireReport: false,
    dashboard: PATH.SALES.path,
    path: PATH.SALES_DETAILED.path
  }
};

/**
 * Returns the modules that do not need specific features for a given mode.
 * @param {string} mode
 * @returns {string[]}
 */
export const getDefaultModulesByMode = (mode: ModeEnum, ignoreModules: ModuleEnum[] = []) =>
  Object.keys(MODULES).filter(
    (module) => MODULES[module].mode === mode && !MODULES[module].features && !ignoreModules.includes(module)
  );

// DEFAULTS
export const DEFAULT_TECHNICIAN_MODULES = getDefaultModulesByMode(INTERVENTION_MODE.TECHNICIAN);
export const DEFAULT_SALES_MODULES = getDefaultModulesByMode(INTERVENTION_MODE.SALES);
export const DEFAULT_PARTS_MODULES = getDefaultModulesByMode(INTERVENTION_MODE.PARTS);
export const ALL_MODULES = Object.keys(MODULES);

export const getReminderableModulesByMode = (mode: ModeEnum) =>
  Object.entries(MODULES).reduce<ModuleEnum[]>((acc, [module, config]) => {
    if (config.canCreateReminder && config.mode === mode) {
      return [...acc, module];
    }
    return acc;
  }, []);

export const getModulesByMode = (mode: ModeEnum) =>
  Object.keys(MODULES).filter(module => MODULES[module].mode === mode);

export const BRANDING_MODULES: ModuleEnum[] = [...DEFAULT_TECHNICIAN_MODULES, BODYWORK_APPOINTMENT, ...DEFAULT_SALES_MODULES, ...DEFAULT_PARTS_MODULES];

const getModulesByExtension = (extension: ExtensionEnum) => Object.keys(MODULES).filter(module => MODULES[module].extensions.includes(extension));

export const getModulesByExtensions = (extensions: ExtensionEnum[] = []) => uniq(extensions.flatMap(getModulesByExtension));

export const getModeByModules = (modules: ModuleEnum[] = []) => uniq(modules.map(module => MODULES[module]?.mode));
